import { createContext, useContext, useMemo, useState } from "react";
import { useAuth } from "@/services/supabase/AuthProvider";

export type SimulatedRole = "SUPER_ADMIN" | "PROJECT_MANAGER" | "AGRONOMIST" | "FIELD_OFFICER" | "DATA_ENTRY" | "FINANCE_OFFICER" | "ACCOUNTANT";

export const permissionMatrix: Record<SimulatedRole, string[]> = {
  SUPER_ADMIN: ["*"],
  PROJECT_MANAGER: ["dashboard.view", "farmer.view", "farmer.edit", "farmer.approve", "farm.view", "farm.edit", "visit.view", "visit.manage", "inputs.view", "distributions.view", "harvest.view", "transactions.view", "media.view", "reports.view", "staff.view", "governance.view"],
  AGRONOMIST: ["dashboard.view", "farmer.view", "farmer.submit", "farm.view", "visit.view", "visit.create", "visit.complete", "inputs.view", "distributions.view", "harvest.view", "media.view", "staff.view"],
  FIELD_OFFICER: ["dashboard.view", "farmer.view", "farmer.create", "farmer.edit", "farmer.submit", "farm.view", "visit.view", "visit.create", "inputs.view", "distributions.view", "distributions.create", "harvest.view", "harvest.create", "media.view", "media.create"],
  DATA_ENTRY: ["dashboard.view", "farmer.view", "farmer.create", "farmer.edit", "farm.view", "inputs.view", "media.view", "media.create"],
  FINANCE_OFFICER: ["dashboard.view", "farmer.view", "harvest.view", "transactions.view", "finance.view", "accounts.view", "payment.prepare", "reports.view"],
  ACCOUNTANT: ["dashboard.view", "transactions.view", "finance.view", "accounts.view", "journal.prepare", "reports.view"],
};

export const canForRole = (role: SimulatedRole, permission?: string) => !permission || permissionMatrix[role].includes("*") || permissionMatrix[role].includes(permission);

interface RoleSimulatorValue {
  role: SimulatedRole;
  permissions: string[];
  setRole: (role: SimulatedRole) => void;
  can: (permission?: string) => boolean;
  isDevelopmentSimulation: boolean;
}

const RoleSimulatorContext = createContext<RoleSimulatorValue | undefined>(undefined);

const ROLE_MAP: Record<string, SimulatedRole> = {
  SUPER_ADMIN: "SUPER_ADMIN",
  PROJECT_MANAGER: "PROJECT_MANAGER",
  ARISE_PROJECT_MANAGER: "PROJECT_MANAGER",
  AGRONOMIST: "AGRONOMIST",
  LEAD_AGRONOMIST: "AGRONOMIST",
  FIELD_OFFICER: "FIELD_OFFICER",
  DATA_ENTRY: "DATA_ENTRY",
  FINANCE_OFFICER: "FINANCE_OFFICER",
  ACCOUNTANT: "ACCOUNTANT",
};

const DEFAULT_VALUE: RoleSimulatorValue = {
  role: "FIELD_OFFICER",
  permissions: permissionMatrix.FIELD_OFFICER,
  setRole: () => {},
  can: (permission?: string) => canForRole("FIELD_OFFICER", permission),
  isDevelopmentSimulation: false,
};

export const RoleSimulatorProvider = ({ children }: { children: React.ReactNode }) => {
  const [role, setRoleState] = useState<SimulatedRole>(() => (localStorage.getItem("aczon_simulated_role") as SimulatedRole) || "SUPER_ADMIN");
  const permissions = permissionMatrix[role];
  const value = useMemo<RoleSimulatorValue>(() => ({ role, permissions, isDevelopmentSimulation: true, setRole: (next) => { localStorage.setItem("aczon_simulated_role", next); setRoleState(next); }, can: (permission) => canForRole(role, permission) }), [role, permissions]);
  return <RoleSimulatorContext.Provider value={value}>{children}</RoleSimulatorContext.Provider>;
};

export const useRoleSimulator = (): RoleSimulatorValue => {
  const simulatorValue = useContext(RoleSimulatorContext);
  const auth = useAuth();

  if (simulatorValue) return simulatorValue;

  const backendRole = auth?.aczonContext?.role_code;
  const mapped: SimulatedRole = backendRole ? (ROLE_MAP[backendRole] || "FIELD_OFFICER") : "FIELD_OFFICER";
  return {
    role: mapped,
    permissions: permissionMatrix[mapped],
    setRole: () => {},
    can: (permission?: string) => canForRole(mapped, permission),
    isDevelopmentSimulation: false,
  };
};

export const simulatedRoles = Object.keys(permissionMatrix) as SimulatedRole[];
